/**
 * DI-3-F — one file to Azure Blob, three `fetch`es and no SDK (story §2.1
 * `blockUpload.ts`, §4 "no SDK in the browser").
 *
 *   ≤ blockSize   ONE Put Blob — `PUT <blob>` with `x-ms-blob-type:
 *                 BlockBlob` and `Content-MD5` = base64(the parser's MD5), so
 *                 the stored blob carries the digest the ingest compares
 *                 (DI-1-6: no download fallback needed);
 *   > blockSize   Put Block per slice — `PUT <blob>&comp=block&blockid=<id>`,
 *                 ids `base64` of the block index as a 4-byte big-endian
 *                 number (`AAAAAA==`, `AAAAAQ==` … — every id the same
 *                 length, as Azure requires) — then ONE Put Block List —
 *                 `PUT <blob>&comp=blocklist`, the XML of the ids, and
 *                 `x-ms-blob-content-md5` = base64(MD5): the "send Content-MD5
 *                 at commit" finding of DI-1-6 (a block blob otherwise has no
 *                 MD5 and the ingest downloads the whole file to check it).
 *
 * Every request carries `x-ms-version: ${AZURE_REST_VERSION}`; `Content-Length`
 * is the browser's own (a forbidden header for scripts — `fetch` sets it from
 * the Blob body). The slice is read from the `File` when its PUT is granted
 * (`file.slice(a, b)` — a Blob handle, never the whole file in memory).
 *
 * Every PUT goes through the tab-wide queue (`queue.ts`) and is retried up to
 * `PUT_RETRIES` times with back-off (1 s, 2 s, 4 s); a 403 first re-opens the
 * session (an expired SAS). After the last retry the FILE is failed for this
 * pass (`FileUploadFailed`): the manifest still lists it, the ingest reports
 * it `blob_absent`, and the automatic retry pass sends it again.
 */
import { md5HexToBase64 } from "./md5.js";
import { blobUrl } from "./sas.js";
import type { QueueKey, UploadQueue } from "./queue.js";

/**
 * The Blob service REST version every request names. 2020-10-02 is the
 * version Azurite 3.x honours by default (the dev stack's `:10000`) and the
 * one real Azure accepts for Put Block / Put Block List / Put Blob with
 * `x-ms-blob-content-md5`; nothing newer is needed for these three calls.
 */
export const AZURE_REST_VERSION = "2020-10-02";

/** Retries after the first attempt (story: "retries 3× with back-off"). */
export const PUT_RETRIES = 3;

/** Back-off before retry n (1-based): 1 s, 2 s, 4 s. */
export function backoffMs(n: number): number {
  return 1000 * 2 ** (n - 1);
}

/** Block id n: base64 of n as 4 bytes, big-endian ("AAAAAA==", "AAAAAQ=="). */
export function blockId(n: number): string {
  const b = [(n >>> 24) & 255, (n >>> 16) & 255, (n >>> 8) & 255, n & 255];
  return btoa(String.fromCharCode(...b));
}

/** The Put Block List body. */
export function blockListXml(ids: readonly string[]): string {
  return `<?xml version="1.0" encoding="utf-8"?><BlockList>${ids
    .map((id) => `<Latest>${id}</Latest>`)
    .join("")}</BlockList>`;
}

export type FetchLike = (url: string, init: RequestInit) => Promise<Response>;

export class FileUploadFailed extends Error {
  constructor(
    readonly filename: string,
    readonly status: number | null,
  ) {
    super(
      `${filename}: the upload failed after ${String(PUT_RETRIES)} retries${
        status !== null ? ` (HTTP ${String(status)})` : ""
      }`,
    );
  }
}

export interface UploadFileInput {
  file: Blob;
  /** The blob name (the round trip's wire name). */
  filename: string;
  md5Hex: string;
  blockSize: number;
  /** The session's SAS URL (re-read on every attempt: a 403 re-opens it). */
  sasUrl: () => Promise<string>;
  /** A PUT answered 403 on this SAS URL. */
  onForbidden: (sasUrl: string) => void;
  queue: UploadQueue;
  /** The file's place in the queue: `[set order, file order]`. */
  key: QueueKey;
  fetch?: FetchLike;
  sleep?: (ms: number) => Promise<void>;
  /** Bytes of one finished PUT (the board's bytes + rate). */
  onBytes?: (n: number) => void;
  /**
   * DI-3-S75 — the blob's URL from the session's SAS URL; default the
   * session prefix + `filename` (`blobUrl`). A `*_pos.txt` goes to the DA's
   * `aux/` area (`auxBlobUrl`).
   */
  url?: (sasUrl: string) => string;
  /** DI-3-S75 — the stored blob's content type; default `image/jpeg`. */
  contentType?: string;
}

const defaultSleep = (ms: number) =>
  new Promise<void>((r) => {
    setTimeout(r, ms);
  });

/** Upload one file; resolves when its blob is committed (201). */
export async function uploadFile(input: UploadFileInput): Promise<void> {
  const size = input.file.size;
  const md5b64 = md5HexToBase64(input.md5Hex);
  const target = input.url ?? ((sas: string) => blobUrl(sas, input.filename));
  const contentType = input.contentType ?? "image/jpeg";
  if (size <= input.blockSize) {
    await put(input, [...input.key, 0], (url) => ({
      url: target(url),
      init: {
        method: "PUT",
        headers: {
          "x-ms-version": AZURE_REST_VERSION,
          "x-ms-blob-type": "BlockBlob",
          "x-ms-blob-content-type": contentType,
          "Content-MD5": md5b64,
        },
        body: input.file.slice(0, size),
      },
      bytes: size,
    }));
    return;
  }
  const n = Math.ceil(size / input.blockSize);
  const ids = Array.from({ length: n }, (_, i) => blockId(i));
  await Promise.all(
    ids.map((id, i) =>
      put(input, [...input.key, i], (url) => {
        const a = i * input.blockSize;
        const b = Math.min(size, a + input.blockSize);
        return {
          url: `${target(url)}&comp=block&blockid=${encodeURIComponent(id)}`,
          init: {
            method: "PUT",
            headers: { "x-ms-version": AZURE_REST_VERSION },
            body: input.file.slice(a, b),
          },
          bytes: b - a,
        };
      }),
    ),
  );
  await put(
    input,
    [...input.key, n],
    (url) => ({
      url: `${target(url)}&comp=blocklist`,
      init: {
        method: "PUT",
        headers: {
          "x-ms-version": AZURE_REST_VERSION,
          "x-ms-blob-content-md5": md5b64,
          "x-ms-blob-content-type": contentType,
          "Content-Type": "application/xml",
        },
        body: blockListXml(ids),
      },
      bytes: 0,
    }),
    { abortable: false },
  );
}

interface Req {
  url: string;
  init: RequestInit;
  bytes: number;
}

async function put(
  input: UploadFileInput,
  key: QueueKey,
  build: (sasUrl: string) => Req,
  opts: { abortable: boolean } = { abortable: true },
): Promise<void> {
  const doFetch = input.fetch ?? ((u, i) => fetch(u, i));
  const sleep = input.sleep ?? defaultSleep;
  let last: number | null = null;
  // DI-3-S68 F13 — the bytes PUTs may be aborted by the queue's hold (rows
  // first); the commit (no bytes) never is
  const abortable = opts.abortable;
  for (let attempt = 0; attempt <= PUT_RETRIES; attempt++) {
    if (attempt > 0) await sleep(backoffMs(attempt));
    const sas = await input.sasUrl();
    const req = build(sas);
    const ok = await input.queue.run(
      key,
      async (signal?: AbortSignal): Promise<boolean | "aborted"> => {
        try {
          const res = await doFetch(
            req.url,
            signal ? { ...req.init, signal } : req.init,
          );
          last = res.status;
          if (res.status === 403) input.onForbidden(sas);
          return res.status >= 200 && res.status < 300;
        } catch {
          if (signal?.aborted) return "aborted";
          last = null;
          return false;
        }
      },
      { abortable },
    );
    if (ok === "aborted") {
      // replayed once the hold ends (the queue grants no slot before):
      // not an attempt — never counts toward PUT_RETRIES / the file's fail
      attempt -= 1;
      continue;
    }
    if (ok) {
      if (req.bytes > 0) {
        input.queue.addBytes(req.bytes);
        input.onBytes?.(req.bytes);
      }
      return;
    }
  }
  throw new FileUploadFailed(input.filename, last);
}
